import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import ScrollReveal from '../components/ScrollReveal'
import './Sector.css'
import '../styles/shared-hero.css'

interface SectorData {
  name: string
  description: string
  landscape: string
  challenges: string[]
  opportunities: string
}

const sectors: Record<string, SectorData> = {
  healthcare: {
    name: 'Healthcare',
    description: 'From NHS digital transformation to healthtech startups, the healthcare sector demands technology leadership that understands regulatory complexity, patient safety, and the pace of medical innovation.',
    landscape: 'Healthcare technology spans electronic health records, telemedicine platforms, medical device software, and AI diagnostics. The UK healthcare market combines public sector transformation with a growing private healthtech ecosystem, requiring solutions that balance innovation with stringent safety and privacy requirements.',
    challenges: [
      'Navigating NHS procurement frameworks and regulatory compliance (MHRA, CQC)',
      'Ensuring data security and patient privacy while enabling interoperability',
      'Integrating with legacy NHS systems and electronic health records',
      'Balancing innovation speed with medical device regulation and clinical safety'
    ],
    opportunities: 'The convergence of AI, wearable technology, and remote care creates unprecedented opportunities. Companies that can navigate regulatory complexity while delivering intuitive, secure solutions are positioned to transform healthcare delivery and capture significant market share.'
  },
  fintech: {
    name: 'Fintech',
    description: 'Financial technology moves fast. From payment platforms to wealth management, fintech companies need technical leadership that balances innovation with regulatory rigour and security.',
    landscape: 'The UK fintech sector leads globally, with particular strengths in payments, open banking, and insurtech. London remains a hub, but innovation happens nationwide. The regulatory environment through the FCA provides both framework and opportunity for companies that can navigate it effectively.',
    challenges: [
      'Meeting FCA compliance requirements while maintaining development velocity',
      'Building secure, resilient systems that handle financial transactions at scale',
      'Integrating with legacy banking infrastructure and payment networks',
      'Attracting technical talent in a competitive market with high security requirements'
    ],
    opportunities: 'Open banking, embedded finance, and AI-powered financial advice represent massive growth areas. Companies that can build robust, compliant platforms while delivering excellent user experiences will define the next generation of financial services.'
  },
  cyber: {
    name: 'Cybersecurity',
    description: 'In an era of increasing digital threats, cybersecurity companies need technical leaders who understand both offensive and defensive security, compliance requirements, and the human factors in security architecture.',
    landscape: 'The UK cybersecurity sector ranges from enterprise security software to specialised consultancies serving critical national infrastructure. With government investment through the National Cyber Security Centre and growing corporate awareness, the market continues to expand across both public and private sectors.',
    challenges: [
      'Building products that stay ahead of evolving threat landscapes',
      'Meeting stringent government and defence security requirements',
      'Translating complex security concepts into usable products and services',
      'Scaling engineering teams with highly specialised security expertise'
    ],
    opportunities: 'The proliferation of connected devices, cloud migration, and AI-powered threats creates constant demand. Companies that can automate security operations, secure critical infrastructure, and protect remote workforces are essential to the modern economy.'
  },
  media: {
    name: 'Media & Entertainment',
    description: 'From streaming platforms to content management systems, media technology requires leadership that understands scale, real-time delivery, and the evolving nature of content consumption.',
    landscape: 'The UK media technology sector spans broadcast technology, streaming platforms, adtech, and content production tools. The industry faces continuous disruption as consumption patterns shift, requiring technology that can adapt quickly while handling massive scale during peak events.',
    challenges: [
      'Building systems that scale from zero to millions during major events',
      'Managing complex content workflows across multiple platforms and formats',
      'Monetising content through advertising, subscriptions, or new models',
      'Protecting intellectual property while enabling legitimate distribution'
    ],
    opportunities: 'The fragmentation of media consumption creates opportunities for platforms that can aggregate, personalise, and deliver content seamlessly. AI-generated content and immersive experiences represent the next frontier for innovation.'
  },
  ecommerce: {
    name: 'E-commerce',
    description: 'Online retail demands technology that converts browsers to buyers, scales through peak seasons, and integrates complex supply chains while delivering exceptional customer experiences.',
    landscape: 'UK e-commerce continues to grow across B2C, B2B, and marketplace models. The sector requires sophisticated technology spanning storefronts, inventory management, payments, logistics, and customer analytics. Competition is fierce, and technical excellence often determines success.',
    challenges: [
      'Building conversion-optimised experiences across web and mobile',
      'Managing inventory and fulfilment across multiple channels and warehouses',
      'Handling traffic spikes during sales events without over-provisioning',
      'Integrating with payment providers, marketplaces, and logistics networks'
    ],
    opportunities: 'The continued shift to online shopping, combined with AI-powered personalisation and sustainable commerce practices, creates opportunities for innovative retailers. B2B e-commerce remains relatively underdeveloped and represents significant growth potential.'
  },
  saas: {
    name: 'SaaS',
    description: 'Software as a Service companies face unique challenges in multi-tenancy, subscription economics, and product-led growth. Technical leadership must balance platform investment with go-to-market speed.',
    landscape: 'The UK SaaS ecosystem spans vertical solutions for industries like construction, legal, and agriculture alongside horizontal tools for productivity, HR, and finance. Success requires building platforms that customers love while maintaining the infrastructure and security expectations of enterprise buyers.',
    challenges: [
      'Designing multi-tenant architectures that scale efficiently',
      'Managing subscription billing, usage tracking, and pricing complexity',
      'Building product-led growth mechanisms into the application',
      'Balancing feature velocity with platform stability and security'
    ],
    opportunities: 'The continued adoption of cloud software across all industries creates massive addressable markets. AI integration, API-first platforms, and vertical SaaS solutions represent the most significant growth opportunities in the coming years.'
  },
  ai: {
    name: 'Artificial Intelligence',
    description: 'AI companies operate at the frontier of technology, requiring leadership that understands machine learning operations, data infrastructure, ethical considerations, and rapid market evolution.',
    landscape: 'UK AI spans from foundational model development to applied solutions in healthcare, finance, and creative industries. The sector attracts significant investment and talent, but also faces scrutiny around ethics, copyright, and societal impact. Technical leadership must navigate both opportunity and responsibility.',
    challenges: [
      'Building reliable ML pipelines that work in production, not just demos',
      'Managing data governance, privacy, and ethical AI considerations',
      'Deploying models cost-effectively at scale with acceptable latency',
      'Hiring and retaining scarce machine learning and MLOps talent'
    ],
    opportunities: 'AI is transforming every industry. Companies that can apply AI to specific domains, automate business processes, or enable new creative possibilities are positioned for extraordinary growth. The UK regulatory approach may create advantages for responsible AI developers.'
  },
  cleantech: {
    name: 'Clean Technology',
    description: 'Climate technology companies combine hardware and software challenges with the urgency of environmental need. Technical leadership must balance innovation speed with capital efficiency and regulatory compliance.',
    landscape: 'UK cleantech spans renewable energy systems, carbon accounting platforms, sustainable materials, and circular economy solutions. Government policy and ESG investment drive demand, while the sector requires sophisticated integration of IoT, data analytics, and often physical hardware.',
    challenges: [
      'Integrating software with physical hardware and IoT sensor networks',
      'Navigating complex regulatory frameworks and certification requirements',
      'Building business models that work without indefinite subsidy',
      'Scaling systems that must operate reliably in challenging environments'
    ],
    opportunities: 'The transition to net-zero creates massive markets across energy, transport, buildings, and industry. Companies that can measure, optimise, and reduce environmental impact through technology are essential to meeting climate commitments and will see sustained demand.'
  },
  edtech: {
    name: 'Education Technology',
    description: 'Edtech bridges pedagogy and technology, requiring leaders who understand learning science, institutional procurement, and the challenge of engaging diverse learners across different contexts.',
    landscape: 'UK education technology serves schools, universities, corporate learning, and direct-to-consumer markets. The sector requires solutions that work within constrained budgets, integrate with existing institutional systems, and demonstrate measurable learning outcomes.',
    challenges: [
      'Designing for diverse learners with varying technical access and abilities',
      'Navigating institutional procurement cycles and budget constraints',
      'Integrating with legacy school and university systems',
      'Demonstrating measurable learning outcomes and return on investment'
    ],
    opportunities: 'The demand for skills-based learning, AI tutoring, and lifelong education continues to grow. Companies that can personalise learning at scale, credential alternative pathways, and serve corporate training needs will capture significant value.'
  },
  property: {
    name: 'PropTech',
    description: 'Property technology transforms how we build, manage, and transact real estate. Technical leadership must understand the intersection of physical assets, financial complexity, and stakeholder management.',
    landscape: 'UK PropTech spans residential and commercial markets, including construction technology, property management platforms, and transaction services. The sector deals with high-value, low-frequency transactions and complex stakeholder relationships between owners, tenants, agents, and service providers.',
    challenges: [
      'Integrating with fragmented property data sources and legacy systems',
      'Building trust in high-value transactions with multiple stakeholders',
      'Managing the complexity of property data, regulations, and compliance',
      'Scaling through slow property cycles and market volatility'
    ],
    opportunities: 'The pressure for sustainable buildings, efficient space usage, and streamlined transactions creates ongoing demand. AI-powered valuations, smart building technology, and embedded property finance represent significant opportunities for innovation.'
  },
  legal: {
    name: 'Legal Technology',
    description: 'Legal tech transforms a conservative, knowledge-intensive industry. Technical leaders must understand professional ethics, complex workflows, and the balance between automation and human judgment.',
    landscape: 'UK legal technology serves law firms, in-house legal teams, and consumers. The sector requires sophisticated document analysis, workflow automation, and compliance tools. Professional regulations and partnership structures create unique go-to-market challenges alongside technical complexity.',
    challenges: [
      'Building AI that handles legal complexity while managing hallucination risks',
      'Navigating SRA regulations and professional liability considerations',
      'Integrating with established practice management systems',
      'Overcoming cultural resistance to technology adoption in traditional firms'
    ],
    opportunities: 'The access to justice gap and pressure on legal costs create demand for scalable solutions. Document automation, legal research AI, and litigation analytics represent significant opportunities, particularly as large language models improve legal reasoning capabilities.'
  },
  logistics: {
    name: 'Logistics & Supply Chain',
    description: 'Supply chain technology orchestrates complex physical movements with data. Leaders must understand operational constraints, real-time optimisation, and the challenge of connecting fragmented systems.',
    landscape: 'UK logistics technology spans last-mile delivery, warehouse automation, freight platforms, and supply chain visibility. The sector requires real-time coordination across multiple parties, integration with physical infrastructure, and resilience through disruption.',
    challenges: [
      'Building real-time optimisation that accounts for operational constraints',
      'Integrating with legacy systems across multiple supply chain partners',
      'Managing data quality and visibility across complex networks',
      'Scaling through economic cycles and supply chain disruptions'
    ],
    opportunities: 'The push for supply chain resilience, sustainability tracking, and last-mile efficiency creates sustained demand. AI-powered forecasting, autonomous logistics, and blockchain-enabled transparency represent the next generation of supply chain technology.'
  }
}

function Sector({ sectorKey }: { sectorKey: string }) {
  const sector = sectors[sectorKey]
  
  if (!sector) {
    return null
  }

  return (
    <>
      <SEO 
        title={`Fractional CTO ${sector.name}`}
        description={`Fractional CTO services for ${sector.name.toLowerCase()} companies. Strategic technology leadership for scaling your business.`}
        path={`/sectors/${sectorKey}`}
      />
      
      {/* Hero Section */}
      <section className="sector-hero page-hero">
        <div className="page-hero-content sector-hero-content">
          <ScrollReveal>
            <h1>Fractional CTO <em>{sector.name}</em></h1>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <p className="sector-hero-description">
              Strategic technology leadership for {sector.name.toLowerCase()} companies
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Description Section */}
      <section className="sector-section">
        <div className="section-grid">
          <div className="section-label">
            {sector.name}
            <span></span>
          </div>
          <ScrollReveal className="sector-content">
            <h2>{sector.description}</h2>
          </ScrollReveal>
        </div>
      </section>

      {/* Landscape Section */}
      <section className="sector-section sector-section-alt">
        <div className="section-grid">
          <div className="section-label">
            Landscape
            <span></span>
          </div>
          <ScrollReveal className="sector-content">
            <p className="sector-text">{sector.landscape}</p>
          </ScrollReveal>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="sector-section">
        <div className="section-grid">
          <div className="section-label">
            Challenges
            <span></span>
          </div>
          <div className="sector-content">
            <ScrollReveal>
              <p className="sector-intro">
                {sector.name} companies face specific technology challenges that benefit from experienced leadership:
              </p>
            </ScrollReveal>
            <ul className="sector-challenges">
              {sector.challenges.map((challenge, index) => (
                <ScrollReveal key={index} delay={index * 100}>
                  <li>{challenge}</li>
                </ScrollReveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Opportunities Section */}
      <section className="sector-section sector-section-alt">
        <div className="section-grid">
          <div className="section-label">
            Opportunities
            <span></span>
          </div>
          <ScrollReveal className="sector-content">
            <p className="sector-text">{sector.opportunities}</p>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA Section */}
      <section className="sector-cta-section">
        <div className="sector-cta-content">
          <ScrollReveal>
            <h2>Looking for a fractional CTO in {sector.name}?</h2>
            <p>
              We work with {sector.name.toLowerCase()} companies across the UK and beyond. 
              Let's discuss how part-time technology leadership could help your business.
            </p>
            <Link to="/contact" className="cta-button-link">Get in touch</Link>
          </ScrollReveal>
        </div>
      </section>
    </>
  )
}

export default Sector
